import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import {
	type ProjectType,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	toProjectType,
	SOFTWARE_PROJECT,
	projectTypeToCanonicalId,
	CORE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types';

import { COHORT, NOT_ENROLLED, VARIATION } from '@atlassian/jira-common-util-get-experiment-group';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CORE, PRODUCT_DISCOVERY, SERVICE_DESK, SOFTWARE } from '@atlassian/jira-shared-types';

export type SupportedProjectType =
	| typeof SERVICE_DESK_PROJECT
	| typeof PRODUCT_DISCOVERY_PROJECT
	| typeof SOFTWARE_PROJECT;
export const SUPPORTED_PROJECT_TYPES: SupportedProjectType[] = [
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	SOFTWARE_PROJECT,
];

export type UpgradeOnboardingTouchPoint = 'flag' | 'modal';

export const projectToProductKey = (projectType: ProjectType) => {
	const productKey = projectType && projectTypeToCanonicalId(projectType);
	return productKey === PRODUCT_DISCOVERY ? productKey : `${productKey}.ondemand`;
};

export const isSupportedProjectType = (value?: string | null): value is SupportedProjectType => {
	const projectType = toProjectType(value);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return !!projectType && SUPPORTED_PROJECT_TYPES.includes(projectType as SupportedProjectType);
};

const getFlagKey = (projectType: ProjectType) => {
	let flagKey = 'unknown';

	if (projectType === SOFTWARE_PROJECT) {
		flagKey = 'jsw_upgrade_onboarding_experiment';
	} else if (projectType === SERVICE_DESK_PROJECT) {
		flagKey = 'jsm_upgrade_onboarding_experiment';
	} else if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		flagKey = 'jpd_upgrade_onboarding_experiment';
	}

	return flagKey;
};

export const getUpgradeOnboardingCohort = (projectType: ProjectType) => {
	let config;

	switch (projectType) {
		case SOFTWARE_PROJECT:
			[config] = UNSAFE_noExposureExp('jsw_upgrade_onboarding_experiment');
			break;
		case SERVICE_DESK_PROJECT:
			[config] = UNSAFE_noExposureExp('jsm_upgrade_onboarding_experiment');
			break;
		case PRODUCT_DISCOVERY_PROJECT:
			[config] = UNSAFE_noExposureExp('jpd_upgrade_onboarding_experiment');
			break;
		default:
			[config] = UNSAFE_noExposureExp('unknown');
	}

	const cohort = config.get(COHORT, NOT_ENROLLED);

	return cohort;
};

export const fireUpgradeOnboardingExperimentExposure = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	projectType: ProjectType,
	touchPoint: UpgradeOnboardingTouchPoint,
) => {
	let cohort;
	const flagKey = getFlagKey(projectType);

	// send Statsig
	switch (projectType) {
		case SOFTWARE_PROJECT:
			cohort = expVal('jsw_upgrade_onboarding_experiment', 'cohort', 'not-enrolled');
			break;
		case SERVICE_DESK_PROJECT:
			cohort = expVal('jsm_upgrade_onboarding_experiment', 'cohort', 'not-enrolled');
			break;
		case PRODUCT_DISCOVERY_PROJECT:
			cohort = expVal('jpd_upgrade_onboarding_experiment', 'cohort', 'not-enrolled');
			break;
		default:
			cohort = expVal('unknown', 'cohort', 'not-enrolled');
	}

	// send GAS event
	fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', {
		flagKey,
		value: cohort,
		growthInitiative: 'transmuters',
		touchPoint,
	});
};

export const isOnVariationForUpgradeOnboarding = (projectType: ProjectType) =>
	getUpgradeOnboardingCohort(projectType) === VARIATION;

export const projectTypeToApplicationKey = (projectType: ProjectType) => {
	switch (projectType) {
		case SOFTWARE_PROJECT:
			return SOFTWARE;
		case SERVICE_DESK_PROJECT:
			return SERVICE_DESK;
		case CORE_PROJECT:
			return CORE;
		case PRODUCT_DISCOVERY_PROJECT:
			return PRODUCT_DISCOVERY;
		default:
			return CORE;
	}
};

export const getOfferingProductKey = (projectType: ProjectType) => {
	let flagKey = 'unknown';

	if (projectType === SOFTWARE_PROJECT) {
		flagKey = 'Jira Software (Cloud)';
	} else if (projectType === SERVICE_DESK_PROJECT) {
		flagKey = 'Jira Service Management (Cloud)';
	} else if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		flagKey = 'Jira Product Discovery';
	}

	return flagKey;
};
